import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { getRoleName, isInvitedMember, useAuth } from '../context/AuthContext';
import { apiFetch } from '../services/api';
import SEO from '../components/SEO';
import {
  LayoutDashboard, Brain, ShieldCheck, BarChart3, Users, Code, Headphones,
  ChevronLeft, ChevronRight, CreditCard, type LucideIcon,
  Receipt,
} from 'lucide-react';
import '../styles/DashboardLayout.scss';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  badge?: string;
  ownerOnly?: boolean;
  publisherOnly?: boolean;
  demoVisible?: boolean;
}

const navItems: NavItem[] = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'My Dashboard' },
  { to: '/dashboard/ai-check', icon: Brain, label: 'AI Checker', badge: 'NEW' },
  { to: '/dashboard/plagiarism', icon: ShieldCheck, label: 'Plagiarism Check' },
  { to: '/dashboard/usage', icon: BarChart3, label: 'Usage & History' },
  { to: '/dashboard/team', icon: Users, label: 'Team', ownerOnly: true, demoVisible: true },
  { to: '/dashboard/api-keys', icon: Code, label: 'API Keys', ownerOnly: true, publisherOnly: true },
  // { to: '/dashboard/api-docs', icon: Code, label: 'API Docs' },
  { to: '/dashboard/subscription', icon: CreditCard, label: 'Subscription', ownerOnly: true, demoVisible: true },
  { to: '/dashboard/billing', icon: Receipt, label: 'Billing', ownerOnly: true, demoVisible: true },
  { to: '/dashboard/tickets', icon: Headphones, label: 'Support' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const [/* planName */, setPlanName] = useState('');

  useEffect(() => {
    apiFetch('/payments/my-subscription/')
      .then((data: any) => setPlanName(data?.plan_name || ''))
      .catch(() => setPlanName(''));
  }, [user]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) {
        setDropOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const initials = user?.full_name
    ? user.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.slice(0, 2).toUpperCase() || 'MP';

  const roleLabel = getRoleName(user?.role)
    ? getRoleName(user?.role).charAt(0).toUpperCase() + getRoleName(user?.role).slice(1)
    : '';
  const invitedMember = isInvitedMember(user?.role);
  const isPublisher = getRoleName(user?.role).toLowerCase() === 'publisher';

  const normalizedPlan: any = roleLabel.trim().toLowerCase();

  const isDemoPlan = normalizedPlan === 'demo';

  const isFreeplan = !['free', 'demo'].includes(normalizedPlan);

  const visibleNavItems = navItems.filter(item => {
    const hiddenForInvited = item.ownerOnly && invitedMember && !(isDemoPlan && item.demoVisible);
    const hiddenForPublisher = item.publisherOnly && !(isPublisher && isFreeplan);
    return !hiddenForInvited && !hiddenForPublisher;
  });

  return (
    <div className="layout">
      <SEO title="Client Portal" noIndex />

      {/* ── Sidebar ─────────────────────────────────────────── */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* Logo row */}
        <div className="logoRow">
          <Link to="/dashboard" className="logoLink">
            <div className="logoBadge">
              <img
                src="/manna-plag-favicon.png"
                alt="Manna Plag"
                className="logoImg"
                onError={e => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                  svg.setAttribute('width', '16'); svg.setAttribute('height', '16');
                  svg.setAttribute('viewBox', '0 0 16 16'); svg.setAttribute('fill', 'none');
                  svg.innerHTML = '<path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="white" stroke-width="1.5" fill="none"/><path d="M8 5L11 7V11L8 13L5 11V7L8 5Z" fill="white"/>';
                  e.currentTarget.parentElement?.appendChild(svg);
                }}
              />
            </div>
            {!collapsed && (
              <div className="logoTextWrap">
                <p className="logoTitle">Manna Plag</p>
                <p className="logoSubtitle">Research Integrity</p>
              </div>
            )}
          </Link>
          <button onClick={() => setCollapsed(!collapsed)} className="collapseBtn">
            {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </button>
        </div>

        {/* Nav items */}
        <nav className="nav">
          {visibleNavItems.map(item => {
            const active = item.to === '/dashboard'
              ? location.pathname === '/dashboard'
              : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                title={collapsed ? item.label : undefined}
                className={`navItem ${active ? 'navItemActive' : ''}`}
              >
                <item.icon size={19} className="navIcon" aria-hidden="true" />
                {!collapsed && (
                  <>
                    <span className="navLabel">{item.label}</span>
                    {item.badge && (
                      <span className="navBadge">{item.badge}</span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="logoutWrap">
          <button onClick={handleLogout} className="logoutBtn">
            <i className="ti ti-logout logoutIcon" aria-hidden="true" />
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────────────── */}
      <div className="main">

        {/* ── WHITE topbar ────────────────────────────────── */}
        <header ref={dropRef} className="topbar">
          {/* Left — logo wordmark (visible when sidebar collapsed) */}
          {collapsed && (
            <div className="topbarWordmark">
              <p className="topbarWordmarkText">Manna Plag</p>
            </div>
          )}
          {!collapsed && (
            <div className="topbarLeftSpacer" />
          )}

          {/* Right — user */}
          <div className="topbarRight">

            {/* User pill */}
            <button onClick={() => setDropOpen(!dropOpen)} className="userPill">
              <div className="avatar">
                {initials}
              </div>
              <div className="userInfo">
                <p className="userName">
                  {user?.full_name || user?.email}
                </p>
                <p className="userRole">{roleLabel}</p>
              </div>
              <i
                className={`ti ${dropOpen ? 'ti-chevron-up' : 'ti-chevron-down'} chevron`}
                aria-hidden="true"
              />
            </button>

            {/* Dropdown */}
            {dropOpen && (
              <div className="dropdown">
                <div className="dropdownHeader">
                  <p className="dropdownName">
                    {user?.full_name}
                  </p>
                  <p className="dropdownEmail">
                    {user?.email}
                  </p>
                </div>
                {[
                  { to: '/dashboard/profile', icon: 'ti-user-circle', label: 'My profile' },
                ].map(a => (
                  <Link
                    key={a.to} to={a.to} onClick={() => setDropOpen(false)}
                    className="dropdownLink"
                  >
                    <i className={`ti ${a.icon} dropdownIcon`} aria-hidden="true" />
                    {a.label}
                  </Link>
                ))}
                <div className="dropdownFooter">
                  <button onClick={handleLogout} className="dropdownSignout">
                    <i className="ti ti-logout" style={{ fontSize: 15 }} aria-hidden="true" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <div className="pageContent">
          <Outlet />
        </div>
      </div>
    </div>
  );
}